import { filterNullish } from '@modernhealth/filter-nullish';
import { useCallback, useEffect, useMemo } from 'react';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';

import { useRemoveProviderMutation } from 'src/modules/providers/documents/removeProviderMutation.generated';
import { capture } from 'src/shared/helpers/sentryCapture';
import { captureException } from 'src/shared/helpers/captureException';
import { useMatchedProvidersMutation } from 'src/modules/providers/documents/matchedProviders.generated';
import * as Sentry from '@sentry/browser';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'src/config/rootStore';
import {
    getMyCoaches,
    getMyTherapists,
    getMyProviders,
    getMyProviderAssignments,
} from 'src/modules/providers/selectors/providersSelectors';
import { CareOption } from 'src/generated/webapp_gql';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { useTranslation } from 'react-i18next';
import { useChooseProviderMutation } from 'src/modules/providers/documents/chooseProviderMutation.generated';
import { useDoesUserQualifyForTherapy } from 'src/modules/user/hooks/userHooks';

import { GetMyProvidersDocument } from '../documents/getMyProvidersQuery.generated';

interface MatchVariables {
    providerType: CareOption;
    careRecommendationUuid: string;
    races?: string[];
}

interface useMatchedProviderProps {
    skip?: boolean;
    matchVariables?: MatchVariables;
}

export enum ProviderMatchErrorString {
    NO_CLIENT_CAPACITY = 'no longer has client capacity',
    MAX_COACH_ASSIGNMENTS = 'Can not add Coach, maximum amount of assignments reached',
    MAX_THERAPIST_ASSIGNMENTS = 'Can not add Therapist, maximum amount of assignments reached',
}

export const useProvider = () => {
    const dispatch = useDispatch<Dispatch>();
    const doesUserQualifyForTherapy = useDoesUserQualifyForTherapy();
    const { t } = useTranslation();

    const [chooseProviderMutation, { loading: isAddingProvider }] = useChooseProviderMutation({
        refetchQueries: [{ query: GetMyProvidersDocument }],
        // Wait until we re-fetch the user's provider list
        awaitRefetchQueries: true,
    });
    const [removeProviderMutation, { loading: isRemovingProvider }] = useRemoveProviderMutation({
        refetchQueries: [{ query: GetMyProvidersDocument }],
        // Wait until we re-fetch the user's provider list
        awaitRefetchQueries: true,
    });

    const myProviderAssignments = useSelector(getMyProviderAssignments);
    const myProviders = useSelector(getMyProviders);
    const myCoaches = useSelector(getMyCoaches);
    const myTherapists = useSelector(getMyTherapists);

    const removeProvider = useCallback(
        async (providerUuid: string) => {
            try {
                await removeProviderMutation({
                    variables: { providerUuid },
                });
            } catch (error) {
                capture(error);
                captureException(error);
                throw error;
            }
        },
        [removeProviderMutation],
    );

    const addProvider = useCallback(
        async (providerUuid: string, uuid: string) => {
            try {
                await chooseProviderMutation({
                    variables: {
                        providerUuid,
                        uuid,
                    },
                });
            } catch (error) {
                capture(error);
                captureException(error);

                const errorString = `${error}`;
                if (errorString.includes(ProviderMatchErrorString.NO_CLIENT_CAPACITY)) {
                    toastNotificationsService.renderErrorToast(
                        t('providers:mental_health_coach.notification.capacity'),
                    );
                } else if (errorString.includes(ProviderMatchErrorString.MAX_COACH_ASSIGNMENTS)) {
                    toastNotificationsService.renderErrorToast(
                        t('providers:coach.notification.max_coaches'),
                    );
                } else if (
                    errorString.includes(ProviderMatchErrorString.MAX_THERAPIST_ASSIGNMENTS)
                ) {
                    toastNotificationsService.renderErrorToast(
                        t('providers:provider_selection.notification.max_therapists'),
                    );
                } else {
                    toastNotificationsService.renderErrorToast(config.messages.genericError);
                }

                throw error;
            }
        },
        [chooseProviderMutation, t],
    );

    const fetchMyProviders = useCallback(async () => {
        return dispatch.providers.fetchMyProviders();
    }, [dispatch.providers]);

    return {
        removeProvider,
        addProvider,
        fetchMyProviders,
        myProviderAssignments,
        myProviders,
        myCoaches,
        myTherapists,
        isRemovingProvider,
        isAddingProvider,
        numberOfCoachesPerMember: 2,
        doesUserQualifyForTherapy,
    };
};

export const useMatchedProvider = ({
    skip = false,
    matchVariables,
}: useMatchedProviderProps = {}) => {
    const hookResult = useProvider();

    const memoizedMatchVariables = useDeepCompareMemoize(matchVariables);

    const [matchedProviders, { data, loading, called, error: matchedProvidersError }] =
        useMatchedProvidersMutation({});

    const fetchProviders = useCallback(
        async (matchVariables: MatchVariables) => {
            try {
                const { careRecommendationUuid, races, providerType } = matchVariables;
                const res = await matchedProviders({
                    awaitRefetchQueries: true,
                    refetchQueries: [{ query: GetMyProvidersDocument }],
                    variables: {
                        careRecommendationUuid,
                        pagination: {
                            pageNumber: 1,
                            pageSize: 9,
                        },
                        preferences: {
                            races,
                        },
                        providerTypeOverride: providerType,
                    },
                });

                if (!res.data?.matchedProviders?.providerMatchAttempt?.uuid) {
                    Sentry.withScope(scope => {
                        scope.setExtra('error_info', {
                            message: 'No provider match attempt uuid',
                            data: res.data,
                        });
                        scope.setTag('error.source', 'useMatchedProvider');

                        capture('Failed to fetch providers');
                    });
                }

                return res?.data;
            } catch (error) {
                Sentry.withScope(scope => {
                    scope.setExtra('error_info', 'Failed to fetch matched providers.');
                    scope.setTag('error.source', 'useMatchedProvider');

                    capture(error);
                });
                captureException(error, {
                    error_info: 'Failed to fetch matched providers.',
                    'error.source': useMatchedProvider,
                });

                return null;
            }
        },
        [matchedProviders],
    );

    const matchedPreferenceProviders = useMemo(() => {
        return data?.matchedProviders?.providersMatchedPreferences
            ? filterNullish(data.matchedProviders.providersMatchedPreferences)
            : undefined;
    }, [data?.matchedProviders?.providersMatchedPreferences]);

    const notMatchedPreferenceProviders = useMemo(() => {
        return data?.matchedProviders?.providersDidNotMatchPreferences
            ? filterNullish(data.matchedProviders.providersDidNotMatchPreferences)
            : undefined;
    }, [data?.matchedProviders?.providersDidNotMatchPreferences]);

    useEffect(() => {
        if (memoizedMatchVariables && !skip) {
            fetchProviders(memoizedMatchVariables);
        }
    }, [memoizedMatchVariables, fetchProviders, skip]);

    return {
        ...hookResult,
        refetchMatchedProviders: fetchProviders,
        matchedPreferenceProviders,
        notMatchedPreferenceProviders,
        isMatchedProvidersLoading: loading || !called,
        matchedProvidersError,
        allowsFiltering: data?.matchedProviders?.allowsFiltering,
        matchedAttempt: (data?.matchedProviders?.providerMatchAttempt?.uuid ?? '') as string,
    };
};
